/**
 * Branching node-map generation (deterministic).
 *
 * Generates a Slay-the-Spire-style layered DAG: `rows` rows of nodes, edges
 * only go downward (row r -> row r+1), every path is traversable to the boss,
 * and node kinds follow weighted rules per act. Fully RNG-driven so a seed
 * reproduces the exact map.
 */

import { Rng } from "./rng";
import type { MapNode, MapState, NodeKind } from "./types";

export const MAP_TUNING = {
  rows: 12, // content rows before the boss row
  minCols: 2,
  maxCols: 4,
  // Reserved rows: row 0 always combat; a rest appears two rows before boss.
  // Combat rows below this index draw from the act's `combatEarly` pool; rows at
  // or beyond it draw from the harder `combatLate` pool — an intra-act curve.
  lateCombatRow: 6,
} as const;

export interface ActEncounters {
  /** Weak encounters for the early rows of the act. */
  combatEarly: string[][];
  /** Harder encounters for the later rows of the act. */
  combatLate: string[][];
  elite: string[][];
  boss: string;
}

/**
 * Encounter pools per act. The roster + multiplicities are tuned for a rising
 * difficulty curve: Act 1 teaches, Act 2 pressures the Trace clock, Act 3 is
 * lethal. Each act ends in a mechanically distinct boss (see ice.ts).
 */
export const ACT_ENCOUNTERS: Record<number, ActEncounters> = {
  1: {
    combatEarly: [
      ["sentry"],
      ["wisp", "wisp"],
      ["spider"],
      ["sentry", "wisp"],
      ["glitcher"],
      ["probe", "wisp"],
      ["mauler"],
    ],
    combatLate: [
      ["sentry", "spider"],
      ["daemon"],
      ["leech", "wisp"],
      ["glitcher", "glitcher"],
      ["mauler", "probe"],
    ],
    elite: [["bulwark"], ["hive"]],
    boss: "warden",
  },
  2: {
    combatEarly: [
      ["daemon"],
      ["shade"],
      ["tracer"],
      ["enforcer"],
      ["wisp", "wisp", "wisp"],
      ["warden_shard", "probe"],
    ],
    combatLate: [
      ["enforcer", "tracer"],
      ["daemon", "shade"],
      ["corruptor"],
      ["shade", "shade"],
      ["warden_shard", "warden_shard"],
    ],
    elite: [["bulwark"], ["overseer"], ["hive"]],
    boss: "sovereign",
  },
  3: {
    combatEarly: [
      ["reaper"],
      ["juggernaut"],
      ["corruptor"],
      ["daemon", "daemon"],
    ],
    combatLate: [
      ["reaper", "corruptor"],
      ["juggernaut", "tracer"],
      ["warbeast"],
      ["reaper", "shade", "shade"],
    ],
    elite: [["overseer"], ["warbeast"], ["bulwark", "tracer"]],
    boss: "root",
  },
};

function nodeId(act: number, row: number, col: number): string {
  return `a${act}-r${row}-c${col}`;
}

/**
 * Decide a node kind for a given row using weighted rules. Row 0 is always
 * combat; the second-to-last content row is always rest; otherwise weighted.
 */
function rollKind(row: number, rows: number, rng: Rng): NodeKind {
  if (row === 0) return "combat";
  if (row === rows - 2) return "rest";
  const kinds: NodeKind[] = ["combat", "event", "shop", "rest", "elite", "treasure"];
  // Elites/treasure only appear past the early rows; weights tuned for variety.
  const weights =
    row < 3
      ? [60, 25, 0, 0, 0, 15] // early: combat-heavy, some events/treasure
      : [45, 22, 8, 8, 12, 5]; // later: full mix
  return rng.weightedPick(kinds, weights);
}

/**
 * Generate one act's map. Deterministic given the rng. Guarantees:
 *  - row 0 has >=1 entry node
 *  - each non-final row connects forward to at least one node
 *  - every node in row r+1 is reachable from some node in row r
 *  - a single boss node terminates the act
 */
export function generateMap(act: number, rng: Rng): MapState {
  const rows = MAP_TUNING.rows;
  const grid: MapNode[][] = [];

  // Build node rows.
  for (let r = 0; r < rows; r++) {
    const cols = rng.int(MAP_TUNING.minCols, MAP_TUNING.maxCols);
    const rowNodes: MapNode[] = [];
    for (let c = 0; c < cols; c++) {
      rowNodes.push({
        id: nodeId(act, r, c),
        kind: rollKind(r, rows, rng),
        row: r,
        col: c,
        next: [],
      });
    }
    grid.push(rowNodes);
  }

  // Connect each row to the next, guaranteeing reachability both directions.
  for (let r = 0; r < rows - 1; r++) {
    const cur = grid[r]!;
    const nxt = grid[r + 1]!;

    // 1) Each current node gets at least one downward edge (prefer nearest col).
    for (const node of cur) {
      const target = pickNearest(node.col, nxt, rng);
      if (!node.next.includes(target.id)) node.next.push(target.id);
    }

    // 2) Each next node must be reachable: if some next node has no incoming
    //    edge, wire it from the nearest current node.
    for (const target of nxt) {
      const hasIncoming = cur.some((n) => n.next.includes(target.id));
      if (!hasIncoming) {
        const src = pickNearest(target.col, cur, rng);
        if (!src.next.includes(target.id)) src.next.push(target.id);
      }
    }

    // 3) Add a few extra branches for path variety (deterministic).
    for (const node of cur) {
      if (rng.chance(0.35)) {
        const extra = pickNearest(node.col, nxt, rng);
        if (!node.next.includes(extra.id)) node.next.push(extra.id);
      }
    }
  }

  // Boss node: single node after the last content row; all last-row nodes lead
  // to it.
  const bossId = nodeId(act, rows, 0);
  const bossNode: MapNode = {
    id: bossId,
    kind: "boss",
    row: rows,
    col: 0,
    next: [],
    encounterId: ACT_ENCOUNTERS[act]?.boss ?? "warden",
  };
  for (const node of grid[rows - 1]!) {
    if (!node.next.includes(bossId)) node.next.push(bossId);
  }

  // Flatten to a record + entries.
  const nodes: Record<string, MapNode> = {};
  for (const row of grid) for (const n of row) nodes[n.id] = n;
  nodes[bossId] = bossNode;

  const entries = grid[0]!.map((n) => n.id);

  return {
    act,
    rows: rows + 1,
    nodes,
    entries,
    currentNodeId: null,
    visited: [],
    bossId,
  };
}

/** Pick the node in `candidates` whose col is closest to `col` (ties: rng). */
function pickNearest(col: number, candidates: MapNode[], rng: Rng): MapNode {
  let best: MapNode[] = [];
  let bestDist = Infinity;
  for (const n of candidates) {
    const d = Math.abs(n.col - col);
    if (d < bestDist) {
      bestDist = d;
      best = [n];
    } else if (d === bestDist) {
      best.push(n);
    }
  }
  return best.length === 1 ? best[0]! : rng.pick(best);
}

/** Nodes the runner may move to next. */
export function availableNodes(map: MapState): MapNode[] {
  if (map.currentNodeId == null) {
    return map.entries.map((id) => map.nodes[id]!);
  }
  const cur = map.nodes[map.currentNodeId];
  if (!cur) return [];
  return cur.next.map((id) => map.nodes[id]!).filter(Boolean);
}

/** Is `nodeId` a legal next move from the current position? */
export function isLegalMove(map: MapState, nodeId: string): boolean {
  return availableNodes(map).some((n) => n.id === nodeId);
}

/**
 * Resolve the list of ICE defIds for a node, using the act pools + the node's
 * row to pick the early vs late combat pool (the intra-act difficulty curve).
 * Deterministic given the rng. Boss/elite nodes use their dedicated pools.
 */
export function resolveEncounterFor(
  act: number,
  node: MapNode,
  rng: Rng,
): string[] {
  const pools = ACT_ENCOUNTERS[act] ?? ACT_ENCOUNTERS[1]!;
  if (node.kind === "boss") return [pools.boss];
  if (node.kind === "elite") return rng.pick(pools.elite).slice();
  const late = node.row >= MAP_TUNING.lateCombatRow;
  const pool = late ? pools.combatLate : pools.combatEarly;
  return rng.pick(pool).slice();
}
